import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { setBusy } from "./busy.js";

/**
 * DCTA-124 — Check and sign (UX spec §E.2, §E.9 `.scr.s-sign`; mock-up 17-payees.html `add-sign`): a
 * pushed screen that carries the bank's card for a bank-screen action — here 17's add (§F.2 row 13).
 * It holds one thing at a time: the skeleton while the bank builds the card (one status line for a
 * screen reader), the bank's card, the bank's refusal in a notice (DCTA-124: the Bank card is only what
 * is signed), or the couldn't-reach line. What goes in it is the controller's (app/add-flow.ts); this is
 * only the screen.
 */
/** What a screen reader is told while the bank builds a card (one status line). */
export const LOADING_CARD = "Loading the card";

/** A bank card's skeleton at its heights — the status, a row, the actions — hidden from assistive tech (16's pattern). */
export function cardSkeleton(): HTMLElement {
  return el("div", { class: "signskel", aria: { hidden: "true" } }, [el("span", { class: "sk sk-status" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-acts" })]);
}

export interface CheckSignHandlers {
  /** Its own back (the topbar's chevron). */
  onBack(): void;
}

export class CheckSignView {
  readonly element: HTMLElement;
  readonly back: HTMLButtonElement;
  readonly #body: HTMLElement;
  readonly #note: HTMLElement;
  readonly #slot: HTMLElement;
  /** The screen it is pushed over (17 at `add`): it slides under while this one is on top. */
  readonly #under: HTMLElement | undefined;

  constructor(handlers: CheckSignHandlers, under?: HTMLElement) {
    this.#under = under;
    this.back = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Add a payee" } }, [icon("i-chev-l")]);
    this.back.addEventListener("click", () => handlers.onBack());
    this.#note = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#slot = el("div", { class: "signslot" });
    this.#body = el("div", { class: "body" }, [this.#note, this.#slot, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    this.element = el("div", { class: "scr s-sign off-r", role: "region", aria: { labelledby: "sign-title", hidden: "true" } }, [
      el("div", { class: "topbar" }, [this.back, el("span", { class: "t", id: "sign-title" }, ["Check and sign"]), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]),
      this.#body,
    ]);
    this.element.inert = true;
  }

  get open(): boolean {
    return !this.element.classList.contains("off-r");
  }

  /** Pushed in from the right over the add screen; focus to its back (as every pushed screen). */
  push(): void {
    this.element.classList.remove("off-r");
    this.element.inert = false;
    this.element.removeAttribute("aria-hidden");
    if (this.#under !== undefined) {
      this.#under.classList.add("under");
      this.#under.inert = true;
      this.#under.setAttribute("aria-hidden", "true");
    }
    this.#body.scrollTop = 0;
    this.back.focus({ preventScroll: true });
  }

  /** Off to the right; the add screen comes back (its focus is the caller's). */
  pop(): void {
    if (!this.open) return;
    this.element.classList.add("off-r");
    this.element.inert = true;
    this.element.setAttribute("aria-hidden", "true");
    if (this.#under !== undefined) {
      this.#under.classList.remove("under");
      this.#under.inert = false;
      this.#under.removeAttribute("aria-hidden");
    }
    setBusy(this.#body, this.#note, this.#slot, false, "");
    this.#slot.replaceChildren();
  }

  /** The bank is building the card: a skeleton at the card's heights, hidden from assistive tech, and one status line. */
  building(): void {
    setBusy(this.#body, this.#note, this.#slot, true, LOADING_CARD);
    this.#slot.replaceChildren(cardSkeleton());
  }

  /**
   * What the screen holds now: the card, the bank's refusal, or the couldn't-reach line. `heard`: what a
   * screen reader is told in the status line that said "Loading the card" (WCAG 4.1.3) — focus stays put.
   */
  show(node: HTMLElement, heard = ""): void {
    setBusy(this.#body, this.#note, this.#slot, false, "");
    this.#slot.replaceChildren(node);
    this.#note.textContent = heard;
  }

  /**
   * What the bank said about a card it would not build or run (DCTA-124): its sentences in a notice — the
   * first in bold, as every notice — and its ways on. Not a Bank card: the Bank surface is only what the
   * customer signs and its result.
   */
  said(lines: readonly string[], acts: readonly { readonly label: string; readonly ghost: boolean; readonly onSelect: () => void }[]): void {
    const [lead, ...rest] = lines;
    this.show(
      el("div", { class: "said" }, [
        el("div", { class: "notice" }, [icon("i-alert"), el("div", {}, [el("b", {}, [lead ?? ""]), ...rest.map((l) => ` ${l}`)])]),
        ...(acts.length === 0
          ? []
          : [
              el(
                "div",
                { class: "acts" },
                acts.map((a) => {
                  const b = el("button", { class: a.ghost ? "raw btn ghost" : "raw btn", type: "button" }, [a.label]);
                  b.addEventListener("click", a.onSelect);
                  return b;
                }),
              ),
            ]),
      ]),
      lines.join(" "),
    );
  }
}
